import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Text,
  UnorderedList,
  ListItem,
  Heading,
  VStack
} from '@chakra-ui/react';

const HelpDialog = ({ isOpen, onClose, section = 'main' }) => {
  const getContent = () => {
    switch (section) {
      case 'releases':
        return {
          title: 'Managing Releases',
          content: (
            <VStack spacing={4} align="stretch">
              <Text>
                The Releases section allows you to track and manage software updates and changes to RoText™.
                Here's how to use this section effectively:
              </Text>
              <Accordion allowMultiple>
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="medium">
                      Viewing Releases
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <UnorderedList spacing={2}>
                      <ListItem>View all releases in chronological order, with the most recent at the top</ListItem>
                      <ListItem>Each release shows its version number, release date, and associated changes</ListItem>
                      <ListItem>Changes are organized by categories for easy reference</ListItem>
                      <ListItem>Use the search bar to find specific changes or features</ListItem>
                      <ListItem>Filter releases by status to focus on specific stages of development</ListItem>
                    </UnorderedList>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="medium">
                      Managing Releases (Admin)
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <UnorderedList spacing={2}>
                      <ListItem>Create new releases using the "Add Release" button</ListItem>
                      <ListItem>Edit existing releases by clicking the edit icon</ListItem>
                      <ListItem>Update release status as development progresses</ListItem>
                      <ListItem>Add changelog entries to document specific changes</ListItem>
                      <ListItem>Organize changes by category and impact level</ListItem>
                    </UnorderedList>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="medium">
                      Search and Filter
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <UnorderedList spacing={2}>
                      <ListItem>Use the search bar to find releases by version, name, or content</ListItem>
                      <ListItem>Filter releases by status: Planned, In Development, Ready, Published, or Deprecated</ListItem>
                      <ListItem>Combine search and filters to quickly find specific releases</ListItem>
                      <ListItem>Clear filters to view all releases again</ListItem>
                    </UnorderedList>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </VStack>
          )
        };
      default:
        return {
          title: 'How to Use RoText™',
          content: (
            <VStack spacing={4} align="stretch">
              <Text>
                Welcome to RoText™, your comprehensive communication platform designed specifically for Rotary Clubs. 
                This system streamlines club communications by enabling efficient text messaging and email management 
                for announcements, reminders, and important updates.
              </Text>
              <Text>
                RoText™ prioritizes member privacy through consent management while providing powerful features 
                for organizing contacts, managing groups, and tracking message delivery. Whether you're sending 
                quick updates or managing long-term communication strategies, RoText™ provides the tools you need.
              </Text>
              <Accordion allowMultiple>
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="medium">
                      Navigation Menu
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <Text mb={3}>
                      RoText™ features an organized menu structure for easy access to all functionality:
                    </Text>
                    <UnorderedList spacing={2} mb={4}>
                      <ListItem>
                        <Text fontWeight="medium">Messages</Text>
                        <UnorderedList>
                          <ListItem>Send Messages - Create and send text messages</ListItem>
                          <ListItem>View Message History - Review all sent messages</ListItem>
                        </UnorderedList>
                      </ListItem>
                      <ListItem>
                        <Text fontWeight="medium">Members & Groups</Text>
                        <UnorderedList>
                          <ListItem>Manage Members - View and update member information</ListItem>
                          <ListItem>Manage Groups - Create and modify message groups</ListItem>
                          <ListItem>View Contact Changes - Track member information updates</ListItem>
                        </UnorderedList>
                      </ListItem>
                      <ListItem>
                        <Text fontWeight="medium">Consent</Text>
                        <UnorderedList>
                          <ListItem>Manage Consent Process - Handle member messaging permissions</ListItem>
                          <ListItem>View Consent Form - Review the current consent form</ListItem>
                        </UnorderedList>
                      </ListItem>
                      <ListItem>
                        <Text fontWeight="medium">Email</Text>
                        <UnorderedList>
                          <ListItem>Manage Email Templates - Create and edit email templates</ListItem>
                          <ListItem>View Email History - Track all sent emails</ListItem>
                        </UnorderedList>
                      </ListItem>
                      <ListItem>
                        <Text fontWeight="medium">Account</Text>
                        <UnorderedList>
                          <ListItem>Manage Your Profile - Update your personal information</ListItem>
                          <ListItem>Setup Account Preferences - Customize your experience</ListItem>
                        </UnorderedList>
                      </ListItem>
                    </UnorderedList>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="medium">
                      Sending Messages
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <Text mb={3}>
                      To send messages to your club members:
                    </Text>
                    <UnorderedList spacing={2}>
                      <ListItem>Navigate to Messages → Send Messages</ListItem>
                      <ListItem>Choose recipients (individuals or groups)</ListItem>
                      <ListItem>Compose your message with optional emojis</ListItem>
                      <ListItem>Use the "Test Message" feature to preview</ListItem>
                      <ListItem>Click "Send Now" to deliver your message</ListItem>
                      <ListItem>Track delivery in Message History</ListItem>
                    </UnorderedList>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="medium">
                      Managing Members & Groups
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <Heading size="sm" mb={2}>Members Management</Heading>
                    <UnorderedList spacing={2} mb={4}>
                      <ListItem>View and update member information</ListItem>
                      <ListItem>Monitor consent status</ListItem>
                      <ListItem>Track contact information changes</ListItem>
                      <ListItem>Import/Export member data</ListItem>
                    </UnorderedList>

                    <Heading size="sm" mb={2}>Group Management</Heading>
                    <UnorderedList spacing={2}>
                      <ListItem>Create groups for committees or events</ListItem>
                      <ListItem>Add/remove members from groups</ListItem>
                      <ListItem>Send targeted communications</ListItem>
                      <ListItem>Manage group permissions</ListItem>
                    </UnorderedList>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="medium">
                      Consent Management
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <Text mb={3}>
                      RoText™ prioritizes member privacy through consent management:
                    </Text>
                    <UnorderedList spacing={2}>
                      <ListItem>View and track member consent status</ListItem>
                      <ListItem>Manage the consent collection process</ListItem>
                      <ListItem>Update consent form content</ListItem>
                      <ListItem>Generate consent reports</ListItem>
                      <ListItem>Automated consent expiration tracking</ListItem>
                    </UnorderedList>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="medium">
                      Email System
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <Text mb={3}>
                      Manage email communications through the Email system:
                    </Text>
                    <UnorderedList spacing={2}>
                      <ListItem>Create and manage email templates</ListItem>
                      <ListItem>Track email delivery and opens</ListItem>
                      <ListItem>View email history and analytics</ListItem>
                      <ListItem>Manage email preferences</ListItem>
                    </UnorderedList>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="medium">
                      Account Management
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <Text mb={3}>
                      Manage your RoText™ account settings and preferences:
                    </Text>
                    <UnorderedList spacing={2}>
                      <ListItem>Update your profile information</ListItem>
                      <ListItem>Change your password and security settings</ListItem>
                      <ListItem>Set communication preferences</ListItem>
                      <ListItem>Customize your user interface</ListItem>
                      <ListItem>Configure notification settings</ListItem>
                      <ListItem>Set default message templates and signatures</ListItem>
                    </UnorderedList>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="medium">
                      Best Practices
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <UnorderedList spacing={2}>
                      <ListItem>Keep messages concise and clear</ListItem>
                      <ListItem>Use appropriate groups for targeted communication</ListItem>
                      <ListItem>Always test messages before sending</ListItem>
                      <ListItem>Regularly review and update contact information</ListItem>
                      <ListItem>Monitor consent status and renewals</ListItem>
                      <ListItem>Use templates for consistent communication</ListItem>
                    </UnorderedList>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </VStack>
          )
        };
    }
  };

  const { title, content } = getContent();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="#17458f">{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {content}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default HelpDialog;
